import type {
  PricelistProduct,
  PricelistProductUpdateInput,
} from "@/generated/graphql";
import {
  Button,
  DialogHeader,
  DialogTitle,
  useDialogController,
  useStackDialog,
} from "@k3-tech/react-kit";
import { toast } from "@k3-tech/react-kit";
import { useCallback } from "react";
import PricelistProductDetailTemplate from "../components/PricelistProductDetailTemplate";
import PricelistProductEditForm from "../components/PricelistProductEditForm";
import PricelistProductForm from "../components/PricelistProductForm";
import PricelistProductTable from "../components/PricelistProductTable";
import type { PricelistProductAddPriceFormData } from "../types/PricelistProductAddPriceFormData";
import type { PricelistProductEditFormData } from "../types/PricelistProductEditFormData";
import { usePricelistProductBulkCreate } from "../usecases/usePricelistProductBulkCreate";
import { usePricelistProductCreate } from "../usecases/usePricelistProductCreate";
import { usePricelistProductList } from "../usecases/usePricelistProductList";
import { usePricelistProductUpdate } from "../usecases/usePricelistProductUpdate";

export default function PricelistProductListContainer({
  pricelistID,
  pricelistPeriodID,
}: {
  pricelistID: string;
  pricelistPeriodID: string;
}) {
  const { createDialog, closeDialog, closeAllDialogs } = useStackDialog();
  const { confirm } = useDialogController();

  const {
    data,
    fetching,
    load,
    deletePricelistProduct,
    paginationState,
    setPagination,
    setFilters,
  } = usePricelistProductList({
    page: 1,
    pageSize: 10,
    withCount: true,
    pricelistID,
    pricelistPeriodID,
  });
  const { createPricelistProduct } = usePricelistProductCreate();
  const { updatePricelistProduct } = usePricelistProductUpdate();
  const { bulkCreatePricelistProduct } = usePricelistProductBulkCreate();

  const onFormFilterChange = (values: Record<string, unknown>) => {
    const status = values.status as boolean | undefined;

    values.isActive = status;

    const { status: _, keyword, searchType, ...finalFilters } = values;

    // Map "searchType" and "keyword" so we able to filter either
    // by productCode or productName, default to productCode
    const fieldName: string =
      (typeof searchType === "string" ? searchType : "productCode") ??
      "productCode";
    finalFilters[fieldName] = keyword;

    setFilters({ ...finalFilters });
  };
  

  const handleSubmitAddPrice = useCallback(
    async (items: PricelistProductAddPriceFormData[]) => {
      if (!items.length) return;

      const hasEmptySellingPrice = items.some(
        (item) =>
          item.sellingPrice === undefined ||
          item.sellingPrice === null ||
          typeof item.sellingPrice !== "number" ||
          Number.isNaN(item.sellingPrice) ||
          item.sellingPrice <= 0
            );
      
      if (hasEmptySellingPrice) {
        toast.error("All selling prices must be filled");
        return;
      }      

      try {
        // biome-ignore lint:
        let result;
      
        if (items.length === 1) {
          result = await createPricelistProduct({
            pricelistID,
            pricelistPeriodID,
            productID: items[0].productID,
            sellingPrice: items[0].sellingPrice,
            salesChannelPrice: "0",
          });
        } else {
          result = await bulkCreatePricelistProduct({
            items: items.map((item) => ({
              pricelistID,
              pricelistPeriodID,
              productID: item.productID,
              sellingPrice: item.sellingPrice,
              salesChannelPrice: "0",
            })),
          });
        }
      
        if (result.error) {
          toast.success(result.error.message);
          return;
        }
      
        toast.success("Pricelist Products created");
        load();
        closeDialog();
      
      // biome-ignore lint:
      } catch (err: any) {
        console.error(err);
        toast.error(err.message);
      }      
    },
    [
      pricelistID,
      pricelistPeriodID,
      load,
      closeDialog,
      createPricelistProduct,
      bulkCreatePricelistProduct,
    ]
  );

  const handleSubmitEditPrice = useCallback(
    async (prev: PricelistProduct, formData: PricelistProductEditFormData) => {
      const payload: PricelistProductUpdateInput = {
        ...formData,
        pricelistID,
        pricelistPeriodID,
        productID: prev.productID,
        salesChannelPrice: prev.salesChannelPrice,
      };
      const result = await updatePricelistProduct(payload);
      if (!result.error) {
        toast.success("Pricelist Product updated");
        closeDialog();
        load();
      }
    },
    [pricelistID, pricelistPeriodID, load, updatePricelistProduct, closeDialog]
  );

  const onShowUnfinishedWarning = useCallback(() => {
    let id = "";
    id = createDialog({
      template: (
        <>
          <div className="font-semibold text-base">Unfinished Work</div>
          <div className="mb-4">
            Your work will be <strong className="font-bold">discarded</strong>{" "}
            if you click OK.
          </div>
          <div className="flex items-center justify-end gap-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => closeDialog(id)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={closeAllDialogs}>
              OK
            </Button>
          </div>
        </>
      ),
    });
  }, [createDialog, closeDialog, closeAllDialogs]);

  const handleShowAddPriceDialog = useCallback(() => {
    createDialog({
      template: (
        <>
          <DialogHeader>
            <DialogTitle>Add Price</DialogTitle>
          </DialogHeader>
          <PricelistProductForm
            pricelistID={pricelistID}
            pricelistPeriodID={pricelistPeriodID}
            onSubmit={(data) => handleSubmitAddPrice(data.items)}
            onCancel={onShowUnfinishedWarning}
          />
        </>
      ),
      dialogContentClassName: "!max-w-[unset]",
      onInterruptClosing: () => {
        onShowUnfinishedWarning();
        return false;
      },
    });
  }, [
    pricelistID,
    pricelistPeriodID,
    createDialog,
    handleSubmitAddPrice,
    onShowUnfinishedWarning,
  ]);

  const handleShowDetail = useCallback(
    (pricelistProduct: PricelistProduct) => {
      createDialog({
        template: (
          <>
            <DialogHeader>
              <DialogTitle>{pricelistProduct.product?.productName}</DialogTitle>
            </DialogHeader>
            <PricelistProductDetailTemplate
              data={pricelistProduct as PricelistProduct}
            />
          </>
        ),
      });
    },
    [createDialog]
  );

  const handleShowDelete = useCallback(
    async (pricelistProduct: PricelistProduct) => {
      const ok = await confirm({
        title: `Delete ${pricelistProduct.product?.productName} from Pricelist Product?`,
        description: "This action cannot be undone.",
        destructive: true,
        confirmText: "Delete",
        cancelText: "Cancel",
      });
      if (!ok) return;
      const result = await deletePricelistProduct({
        input: { pricelistProductID: pricelistProduct.pricelistProductID },
      });
      if (!result.error) {
        toast.success("Pricelist Product deleted");
        load();
      }
    },
    [confirm, load, deletePricelistProduct]
  );

  const handleShowEdit = useCallback(
    (pricelistProduct: PricelistProduct) => {
      createDialog({
        template: (
          <>
            <DialogHeader>
              <DialogTitle>Edit Price</DialogTitle>
            </DialogHeader>
            <PricelistProductEditForm
              initialData={{
                pricelistProductID: pricelistProduct.pricelistProductID,
                productName: pricelistProduct.product?.productName ?? "",
                sellingPrice: pricelistProduct.sellingPrice,
              }}
              onSubmit={(formData) =>
                handleSubmitEditPrice(pricelistProduct, formData)
              }
              onCancel={() => closeDialog()}
            />
          </>
        ),
        dialogContentClassName: "!max-w-[unset] w-[768px]",
      });
    },
    [
      createDialog,
      closeDialog,
      handleSubmitEditPrice,
    ]
  );

  return (
    <PricelistProductTable
      data={(data?.pricelistProducts.items ?? []) as PricelistProduct[]}
      rowCount={data?.pricelistProducts?.pagination.totalRows || 0}
      onRefresh={() => load()}
      loading={fetching}
      onFormFilterChange={onFormFilterChange}
      paginationState={paginationState}
      onPaginationChange={setPagination}
      onShowAddProduct={handleShowAddPriceDialog}
      onShowDetail={handleShowDetail}
      onShowDelete={handleShowDelete}
      onShowEdit={handleShowEdit}
      pricelistID={pricelistID}
      pricelistPeriodID={pricelistPeriodID}
    />
  );
}
